import React from 'react';
import ReactSelect, {
  components,
  ControlProps,
  DropdownIndicatorProps,
  SingleValueProps
} from 'react-select';
import cx from 'classnames';
import useCustomSelectTheme from '../../hooks/useCustomSelectTheme';

import styles from './Select.module.scss';

const CustomDropdownIndicator = (props: DropdownIndicatorProps) => {
  const {
    selectProps: { menuIsOpen }
  } = props;

  return (
    <components.DropdownIndicator {...props}>
      <span
        className={cx(styles.customDropdownIndicator, {
          [styles.customDropdownIndicatorFocused]: menuIsOpen
        })}
      />
    </components.DropdownIndicator>
  );
};

const CustomSingleValue = (props: SingleValueProps) => {
  const {
    children,
    selectProps: { menuIsOpen }
  } = props;

  return (
    <components.SingleValue
      className={cx(styles.customSingleValue, {
        [styles.customSingleValueFocused]: menuIsOpen
      })}
      {...props}
    >
      {children}
    </components.SingleValue>
  );
};

interface CustomControlProps extends ControlProps {
  controlClassName?: string;
  icon?: React.ReactNode;
  isBorderless?: boolean;
  isSearchable?: boolean;
}

const CustomControl = (props: CustomControlProps) => {
  const { children, controlClassName, icon, isBorderless, menuIsOpen } = props;

  return (
    <div
      className={cx(styles.customControlContainer, controlClassName)}
      data-testid="select-control-container"
    >
      <span
        className={cx(styles.customControlIcon, {
          [styles.customControlIconBorderless]: isBorderless,
          [styles.customControlIconFocused]: menuIsOpen
        })}
      >
        {icon}
      </span>
      <components.Control
        className={cx(styles.customControl, {
          [styles.customControlBorderless]: isBorderless,
          [styles.customControlFocused]: menuIsOpen
        })}
        {...props}
      >
        {children}
      </components.Control>
    </div>
  );
};

interface ISelectProps extends React.ComponentPropsWithoutRef<ReactSelect> {
  icon?: React.ReactNode;
  isBorderless?: boolean;
  controlClassName?: string;
}

const Select = (props: ISelectProps): JSX.Element => {
  const {
    className,
    controlClassName,
    isBorderless = false,
    isSearchable = false,
    icon,
    components
  } = props;
  const getCustomTheme = useCustomSelectTheme();

  return (
    <ReactSelect
      {...props}
      isSearchable={isSearchable}
      components={{
        DropdownIndicator: CustomDropdownIndicator,
        SingleValue: CustomSingleValue,
        ...(icon && {
          // eslint-disable-next-line react/display-name
          Control: (controlProps: ControlProps) => (
            <CustomControl
              {...controlProps}
              controlClassName={controlClassName}
              icon={icon}
              isBorderless={isBorderless}
            />
          )
        }),
        ...components
      }}
      className={cx(
        styles.reactSelectContainer,
        {
          [styles.reactSelectContainerBorderless]: isBorderless
        },
        className
      )}
      classNamePrefix="reactSelect"
      theme={getCustomTheme}
    />
  );
};

export default Select;
